import { useMemo, type ComponentProps } from "react";

import type { ContextWindowSnapshot } from "~/lib/contextWindow";
import { useProviderUsageRead } from "~/usage/useProviderUsage";
import { useNowMinute } from "../../hooks/useNowMinute";
import type { Thread } from "../../types";
import { ContextWindowMeter } from "./ContextWindowMeter";
import { isSubscriptionProvider, selectComposerPlanUsage } from "./composerPlanUsage";

type MeterProps = ComponentProps<typeof ContextWindowMeter>;

/** The two thread fields plan usage is derived from; keeps the memoized footer off the streaming path. */
export type PlanUsageThread = Pick<Thread, "modelSelection" | "rateLimitStatus">;

/**
 * The composer's context meter, with the thread's plan usage folded into its
 * popover when the thread runs on a personal Codex or Claude account.
 *
 * Only those providers mount the reading hook, so an ollama or CMMD-plan thread
 * issues no CMMD request at all. The hook is the read-only one on purpose: a
 * live refresh can wake a suspended Environment, and a hover is not consent for
 * that. Refresh lives on /usage, which the popover links to.
 */
export function PlanUsageContextWindowMeter(props: {
  readonly usage: ContextWindowSnapshot;
  readonly activeThread: PlanUsageThread | undefined;
  readonly popupSide?: MeterProps["popupSide"];
}) {
  const thread = props.activeThread;
  if (thread && isSubscriptionProvider(thread.modelSelection.provider)) {
    return <SubscriptionPlanUsageMeter {...props} activeThread={thread} />;
  }
  return <ContextWindowMeter usage={props.usage} popupSide={props.popupSide} />;
}

function SubscriptionPlanUsageMeter(props: {
  readonly usage: ContextWindowSnapshot;
  readonly activeThread: PlanUsageThread;
  readonly popupSide?: MeterProps["popupSide"];
}) {
  const { accounts } = useProviderUsageRead();
  // Re-derive once a minute so a window that resets while the popover is idle
  // drops out instead of reporting a period that is already over. The minute
  // is the clock: a window can linger up to a minute past its reset, never longer.
  const nowMinute = useNowMinute();
  const { modelSelection, rateLimitStatus } = props.activeThread;
  const planUsage = useMemo(
    () =>
      selectComposerPlanUsage({
        modelSelection,
        rateLimitStatus,
        usageAccounts: accounts,
        nowMs: new Date(`${nowMinute}:00.000Z`).getTime(),
      }),
    [modelSelection, rateLimitStatus, accounts, nowMinute],
  );
  return (
    <ContextWindowMeter usage={props.usage} popupSide={props.popupSide} planUsage={planUsage} />
  );
}
